import React from 'react';
import { X, Upload, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Categories in both languages
export const CATEGORIES_EN = [
  'ROI & Analytics',
  'Technology',
  'Culture & Strategy',
  'Trends & Insights',
  'Leadership',
  'Case Studies'
];

export const CATEGORIES_TR = [
  'Yatırım Getirisi ve Analitik',
  'Teknoloji',
  'Kültür ve Strateji',
  'Trendler ve İçgörüler',
  'Liderlik',
  'Vaka Çalışmaları'
];

// Quill Editor modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['blockquote', 'code-block'],
    ['link'],
    [{ 'align': [] }],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'blockquote', 'code-block',
  'link',
  'align'
];

const BlogPostForm = ({
  formData,
  setFormData,
  editingPost,
  activeTab,
  setActiveTab,
  imagePreview,
  fileInputRef,
  onImageUpload,
  onSubmit,
  onClose,
  isSaving,
  isUploading
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {editingPost ? 'Edit Post' : 'Create New Post'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={onSubmit} className="space-y-6">
            {/* Language Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                type="button"
                onClick={() => setActiveTab('en')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  activeTab === 'en' 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('tr')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === 'tr' 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Türkçe
                {!formData.title_tr && (
                  <span className="w-2 h-2 bg-orange-500 rounded-full" title="Turkish content missing"></span>
                )}
              </button>
            </div>

            {/* Warning if Turkish content is missing */}
            {activeTab === 'tr' && !formData.title_tr && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                <strong>Note:</strong> Turkish content is empty. Fill in Turkish fields so the post displays correctly when visitors select Turkish language.
              </div>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title">Title ({activeTab.toUpperCase()}) *</Label>
              <Input
                id="title"
                value={activeTab === 'en' ? formData.title_en : formData.title_tr}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  [activeTab === 'en' ? 'title_en' : 'title_tr']: e.target.value
                }))}
                placeholder="Enter post title"
                required={activeTab === 'en'}
              />
            </div>

            {/* Excerpt */}
            <div>
              <Label htmlFor="excerpt">Excerpt ({activeTab.toUpperCase()}) *</Label>
              <Textarea
                id="excerpt"
                value={activeTab === 'en' ? formData.excerpt_en : formData.excerpt_tr}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  [activeTab === 'en' ? 'excerpt_en' : 'excerpt_tr']: e.target.value
                }))}
                placeholder="Brief summary of the post..."
                rows={2}
                required={activeTab === 'en'}
              />
            </div>

            {/* Content - Rich Text Editor */}
            <div>
              <Label>Content ({activeTab.toUpperCase()}) *</Label>
              <div className="mt-2 border rounded-lg overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={activeTab === 'en' ? formData.content_en : formData.content_tr}
                  onChange={(value) => setFormData(prev => ({
                    ...prev,
                    [activeTab === 'en' ? 'content_en' : 'content_tr']: value
                  }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your blog content here..."
                  className="bg-white"
                  style={{ minHeight: '200px' }}
                />
              </div>
            </div>

            {/* Author & Role (only show once, not per language) */}
            {activeTab === 'en' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="author">Author Name *</Label>
                  <Input
                    id="author"
                    value={formData.author}
                    onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                    placeholder="Author name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="authorRole">Author Role (EN)</Label>
                  <Input
                    id="authorRole"
                    value={formData.authorRole_en}
                    onChange={(e) => setFormData(prev => ({ ...prev, authorRole_en: e.target.value }))}
                    placeholder="e.g., Senior Coach"
                  />
                </div>
              </div>
            )}
            {activeTab === 'tr' && (
              <div>
                <Label htmlFor="authorRoleTr">Author Role (TR)</Label>
                <Input
                  id="authorRoleTr"
                  value={formData.authorRole_tr}
                  onChange={(e) => setFormData(prev => ({ ...prev, authorRole_tr: e.target.value }))}
                  placeholder="e.g., Kıdemli Koç"
                />
              </div>
            )}

            {/* Category */}
            <div>
              <Label>Category ({activeTab.toUpperCase()}) *</Label>
              <Select
                value={activeTab === 'en' ? formData.category_en : formData.category_tr}
                onValueChange={(value) => {
                  const categories = activeTab === 'en' ? CATEGORIES_EN : CATEGORIES_TR;
                  const idx = categories.indexOf(value);
                  // Auto-fill the other language
                  if (activeTab === 'en') {
                    setFormData(prev => ({
                      ...prev,
                      category_en: value,
                      category_tr: prev.category_tr || CATEGORIES_TR[idx] || ''
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      category_tr: value,
                      category_en: prev.category_en || CATEGORIES_EN[idx] || ''
                    }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {(activeTab === 'en' ? CATEGORIES_EN : CATEGORIES_TR).map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Image Upload */}
            <div>
              <Label>Featured Image</Label>
              <div className="mt-2 flex items-start gap-4">
                <div 
                  className="w-40 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Image className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                      <span className="text-xs text-gray-500">Upload</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onImageUpload}
                  className="hidden"
                />
                {isUploading && (
                  <span className="text-sm text-gray-500">Uploading...</span>
                )}
              </div>
            </div>

            {/* Status & Featured */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-4 h-4 rounded text-blue-600"
                />
                <Label htmlFor="featured" className="mb-0">Featured Post</Label>
              </div>
              <div>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : (editingPost ? 'Update Post' : 'Create Post')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogPostForm;
